import { useEffect, useState } from 'react'
import { Button } from '@floorfile/ui'
import { Copy, useCopy } from '@floorfile/i18n'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { apiFetch } from '@/lib/api-url'
import { useAuth } from '@/lib/auth'
import { appCopy } from '../copy'

async function post(path: string, body: unknown, fallback: string): Promise<void> {
  const response = await apiFetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const payload = (await response.json().catch(() => ({}))) as { error?: { message?: string } }
  if (!response.ok) throw new Error(payload.error?.message ?? fallback)
}

export function EmailVerificationGate() {
  const copy = useCopy(appCopy)
  const { user, logout } = useAuth()
  const [message, setMessage] = useState<string>()
  const [busy, setBusy] = useState(false)
  // An INVITED account cannot be verified — only its invitation activates it —
  // so the gate must not promise a verification email it will never send
  // (FF-AUDIT-057). Same endpoint; the server re-sends the invitation.
  const invited = user?.status === 'INVITED'

  async function resend() {
    if (!user) return
    setBusy(true)
    setMessage(undefined)
    try {
      await post('/api/v1/auth/resend-verification', { email: user.email }, copy.authCommon.requestCouldNotComplete)
      setMessage(invited ? copy.emailVerification.invitedResendSuccess : copy.emailVerification.resendSuccess)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : copy.emailVerification.resendError)
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <section className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 text-center">
        <h1 className="text-xl font-semibold">{invited ? copy.emailVerification.invitedGateTitle : copy.emailVerification.gateTitle}</h1>
        <p className="text-sm text-muted-foreground">
          <Copy
            of={(invited ? copy.emailVerification.invitedGateDescription : copy.emailVerification.gateDescription)({ email: user?.email ?? '' })}
            components={[<strong key="email" />]}
          />
        </p>
        {message && <p className="text-sm" role="status">{message}</p>}
        <Button className="w-full"
 disabled={busy}
 onClick={() => void resend()}

 >
          {busy ? copy.emailVerification.sending : invited ? copy.emailVerification.invitedResend : copy.emailVerification.resend}
        </Button>
        <Button variant="link" size="inline" className="text-muted-foreground hover:text-foreground"
 onClick={() => void logout()}

 >
          {copy.authCommon.signOut}
        </Button>
      </section>
    </main>
  )
}

export function VerifyEmailPage() {
  const copy = useCopy(appCopy)
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { status, refreshSession } = useAuth()
  const token = params.get('token') ?? ''
  // UIX-1: React owns only the OUTCOME of the request — the thing the URL can
  // never tell us. The starting condition is derived from the token on every
  // render, so nothing is seeded and nothing can drift out of step with the
  // address bar.
  const [outcome, setOutcome] = useState<{ state: 'done' | 'error'; message: string } | undefined>(undefined)
  const state = outcome?.state ?? (token ? 'working' : 'error')
  const message =
    outcome?.message ??
    (token ? copy.emailVerification.verifying : copy.emailVerification.missingToken)

  useEffect(() => {
    if (!token) return
    let active = true
    void post('/api/v1/auth/verify-email', { token }, copy.authCommon.requestCouldNotComplete)
      .then(async () => {
        if (!active) return
        setOutcome({ state: 'done', message: copy.emailVerification.verified })
        if (status === 'authenticated' && (await refreshSession())) {
          navigate('/', { replace: true })
        }
      })
      .catch((error: unknown) => {
        if (!active) return
        setOutcome({
          state: 'error',
          message: error instanceof Error ? error.message : copy.emailVerification.invalid,
        })
      })
    return () => {
      active = false
    }
  }, [copy, navigate, refreshSession, status, token])

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <section className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 text-center">
        <h1 className="text-xl font-semibold">{copy.emailVerification.pageTitle}</h1>
        <p className={state === 'error' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'} role="status">
          {message}
        </p>
        {state === 'done' && status !== 'authenticated' && (
          <Link to="/login" className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            {copy.emailVerification.continueToSignIn}
          </Link>
        )}
        {state === 'error' && (
          <Link to="/login" className="text-sm text-primary hover:underline">{copy.emailVerification.returnToSignIn}</Link>
        )}
      </section>
    </main>
  )
}
